import { inject, Injectable } from '@angular/core';
import { AuthRedirectStorageService, FeatureConfigService, GlobalMessageType } from '@spartacus/core';
import { LoginFormComponentService } from '@spartacus/user/account/components';
import { from } from 'rxjs';
import { take, tap, withLatestFrom } from 'rxjs/operators';
import { OAuthLoadingOverlayService } from '../oauth-loading-overlay/oauth-loading-overlay.service';
import { StorageService } from '../storage/storage.service';

@Injectable()
export class CustomLoginFormComponentService extends LoginFormComponentService {
  private featureConfigServiceCustom = inject(FeatureConfigService);
  private storageService = inject(StorageService);
  private overlayService = inject(OAuthLoadingOverlayService);
  private authRedirectStorageService = inject(AuthRedirectStorageService);

  /**
   * Custom login that prepends `bike:nl:` to the userId
   * without changing what the user sees in the input.
   */
  async loginWithPrefix(nativeForm?: HTMLFormElement, userId?: string) {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const originalUserId = this.form.value.userId;
    const prefixedUserId = userId;

    // Patch Angular form in memory only
    this.form.patchValue({ userId: prefixedUserId });

    if (this.featureConfigServiceCustom.isEnabled('authorizationCodeFlowByDefault') && nativeForm) {
      this.overlayService.setLoginFlag();
      this.overlayService.show();
      // Check CSRF session - if expired, page reloads and submission is skipped.
      const csrfSession = await this.isCsrfSessionAlive();
      if (!csrfSession) return;
      this.action = this.buildLoginAction();
      nativeForm.action = this.action || nativeForm.action;

      // Add hidden input for backend submission
      nativeForm.appendChild(
        Object.assign(document.createElement('input'), {
          type: 'hidden',
          name: 'username', // backend expects this
          value: prefixedUserId,
        })
      );

      this.winRef.localStorage?.setItem('OAUTH_REDIRECT_FLOW_KEY', 'true');
      nativeForm.submit();
      this.busy$.next(true);
    } else {
      // Custom login flow using loginWithCredentials
      this.busy$.next(true);

      from(
        this.auth.loginWithCredentials(
          this.form.value.userId.toLowerCase(), // prefixed in memory
          this.form.value.password
        )
      )
        .pipe(
          withLatestFrom(this.auth.isUserLoggedIn()),
          tap(([_, isLoggedIn]) => this.onSuccess(isLoggedIn))
        )
        .subscribe();
    }

    // Restore Angular form value so user sees what they typed
    this.form.patchValue({ userId: originalUserId });
  }

  // This method add parameters to authorize/login
  private buildLoginAction(): string {
    const baseAction = this.authConfigService?.getCustomLoginFormEndpoint() || '';

    const businessType = (this.storageService.getSessionStorageItem('businessTypeSelected') || '').toLowerCase();
    const country = (this.storageService.getSessionStorageItem('countrySelected') || '').toLowerCase();
    const language = this.storageService.getSessionStorageItem('language') || '';

    const params = new URLSearchParams();

    if (businessType) {
      params.set('businessType', businessType);
    }
    if (country) {
      params.set('country', country);
    }
    if (language) {
      params.set('language', language);
    }

    const query = params.toString();
    return query ? `${baseAction}?${query}` : baseAction;
  }

  getContextFromUrl(url: string): { businessType: string; country: string; language: string } | null {
    if (!url) {
      return null;
    }

    const cleanUrl = url.split('?')[0].split('#')[0];
    const parts = cleanUrl.split('/').filter(Boolean);

    if (parts.length < 3) {
      return null;
    }

    return {
      businessType: parts[0] || '',
      country: parts[1] || '',
      language: parts[2] || '',
    };
  }

  clearRedirectUrlIfContextChanged(): void {
    const currentPath = this.router.url.split('?')[0];
    const currentContext = this.getContextFromUrl(currentPath);

    this.authRedirectStorageService
      .getRedirectUrl()
      .pipe(take(1))
      .subscribe((redirectUrl) => {
        if (!redirectUrl || !currentContext) {
          return;
        }

        const redirectContext = this.getContextFromUrl(redirectUrl);

        if (!redirectContext) {
          return;
        }

        const businessTypeChanged = redirectContext.businessType !== currentContext.businessType;

        const countryChanged = redirectContext.country !== currentContext.country;

        if (businessTypeChanged || countryChanged) {
          // 'Clear redirectUrl due to context change'
          this.authRedirectStorageService.setRedirectUrl(undefined);
        }
      });
  }

  handleShimanoCustomLoginError(): void {
    if (!this.featureConfigServiceCustom.isEnabled('authorizationCodeFlowByDefault')) {
      return;
    }
    const error = this.activatedRoute.snapshot.queryParams['error'];
    if (error) {
      sessionStorage.setItem('preserveLoginErrorMessage', 'true');

      let messageKey;

      if (error === 'account_disabled') {
        messageKey = 'login.userDisabled';
      } else if (error === 'user_inactive' || error === 'user_dummy') {
        messageKey = 'login.userInactive';
      } else if (error === 'bad_credentials') {
        messageKey = 'login.badRequest';
      } else if (error === 'country_restricted') {
        messageKey = 'login.countryRestriction';
      } else if (error === 'b2bunit_inactive') {
        messageKey = 'login.b2bunitInactive';
      } else if (error === 'b2bunit_notfound') {
        messageKey = 'login.b2bunitNotfound';
      } else if (error === 'usertype_restricted') {
        messageKey = 'login.usertypeRestricted';
      }
      this.globalMessage.add(
        {
          key: messageKey,
        },
        GlobalMessageType.MSG_TYPE_ERROR
      );
      this.router.navigate([], {
        queryParams: { error: null },
      });
    }
  }
  // TODO: Temporary workaround for known CSRF token expiry issue on custom login.
  // SAP has confirmed this will be fixed in release 221121.13. https://github.com/SAP/spartacus/pull/21518
  // Remove this workaround once the upgrade to 221121.13 is complete.
  private async isCsrfSessionAlive(): Promise<boolean> {
    try {
      const response = await fetch(this.authConfigService.getCsrfEndpoint(), {
        credentials: 'include',
      });

      if (response.ok) return true;
    } catch (e) {}
    window.location.reload();
    return false;
  }
}
